import {
  Loader2,
  MoreHorizontal,
  Plug,
  RefreshCw,
  RotateCcw,
  Settings2,
  Unplug,
} from 'lucide-react'

import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui'
import type { ConnectorPendingAction } from '@/subsystems/vault/connectors/types'

const primaryButton = 'h-8 rounded-lg bg-[#1a2436] px-[11px] text-[12.5px] font-semibold shadow-none hover:bg-[#2c3a52]'
const outlineButton = 'h-8 rounded-lg border-slate-200 bg-white px-[11px] text-[12.5px] font-semibold shadow-none hover:border-slate-300 hover:bg-[#fafbfc] hover:text-slate-900'

const pendingButtonLabel: Record<ConnectorPendingAction, string> = {
  pull: 'Syncing records',
  remove: 'Unsyncing record',
  sync: 'Syncing',
  reset: 'Resetting workspace',
}

export function FeedAnalysisActions({
  feedId,
  sourceName,
  managedRecordUnit,
  pendingAction,
  onReset,
  onConfigure,
  onDisconnect,
  onPullRecords,
  onRefreshSyncedRecords,
  onSync,
}: {
  feedId: string
  sourceName: string
  managedRecordUnit: { singular: string; plural: string }
  pendingAction?: ConnectorPendingAction
  onReset?: () => void
  onConfigure?: () => void
  onDisconnect?: () => void
  onPullRecords?: () => void
  onRefreshSyncedRecords?: () => void
  onSync?: () => void
}) {
  const controlsBusy = Boolean(pendingAction)
  const recordsPending = pendingAction === 'pull' || pendingAction === 'remove'
  return (
    <div className="mt-auto flex flex-wrap items-center gap-2 pt-0.5">
      {onPullRecords && (
        <Button
          className={primaryButton}
          data-testid={`connector-manage-records-${feedId}`}
          size="sm"
          disabled={controlsBusy}
          onClick={onPullRecords}
        >
          {recordsPending
            ? <Loader2 className="size-[13px] animate-spin" />
            : <Plug className="size-[13px]" />}
          {recordsPending && pendingAction
            ? pendingAction === 'pull'
              ? `Syncing ${managedRecordUnit.plural}`
              : `Unsyncing ${managedRecordUnit.singular}`
            : `Manage ${managedRecordUnit.plural}`}
        </Button>
      )}
      {onSync && (
        <Button className={outlineButton} size="sm" variant="outline" disabled={controlsBusy} onClick={onSync}>
          {pendingAction === 'sync'
            ? <Loader2 className="size-[13px] animate-spin" />
            : <RefreshCw className="size-[13px]" />}
          {pendingAction === 'sync' ? pendingButtonLabel.sync : 'Sync now'}
        </Button>
      )}
      {onConfigure && (
        <Button
          data-testid={`connector-configure-${feedId}`}
          className={outlineButton}
          size="sm"
          variant="outline"
          disabled={controlsBusy}
          onClick={onConfigure}
        >
          <Settings2 className="size-[13px]" /> Configure
        </Button>
      )}
      {(onReset || onDisconnect || onRefreshSyncedRecords) && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              aria-label={`More actions for ${sourceName}`}
              data-testid={`connector-actions-${feedId}`}
              className="ml-auto size-8 rounded-lg border-slate-200 bg-white p-0 text-slate-500 shadow-none hover:border-slate-300 hover:bg-[#fafbfc] hover:text-slate-600"
              size="icon-sm"
              variant="outline"
            >
              <MoreHorizontal className="size-[15px]" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            className="min-w-[186px] rounded-[9px] border-slate-200 p-[5px] shadow-[0_10px_30px_rgba(15,23,42,0.14)]"
          >
            {onRefreshSyncedRecords && (
              <>
                <DropdownMenuItem
                  className="rounded-[7px] px-2.5 py-2 text-[12.5px] font-semibold"
                  disabled={controlsBusy}
                  onClick={onRefreshSyncedRecords}
                >
                  {pendingAction === 'sync'
                    ? <Loader2 className="size-[13px] animate-spin" />
                    : <RefreshCw className="size-[13px]" />}
                  {pendingAction === 'sync' ? pendingButtonLabel.sync : 'Refresh synced records'}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
              </>
            )}
            {/* cm-coding: Reset and disconnect remove imported state for this
                feed. Keep both actions in one secondary menu so routine sync
                controls cannot trigger them accidentally, and retain each
                feed-scoped confirmation before destructive work begins. */}
            {onReset && (
              <DropdownMenuItem
                data-testid={`connector-reset-${feedId}`}
                variant="destructive"
                className="rounded-[7px] px-2.5 py-2 text-[12.5px] font-semibold focus:bg-red-50"
                disabled={controlsBusy}
                onClick={onReset}
              >
                {pendingAction === 'reset'
                  ? <Loader2 className="size-[13px] animate-spin" />
                  : <RotateCcw className="size-[13px]" />}
                {pendingAction === 'reset' ? pendingButtonLabel.reset : 'Reset workspace'}
              </DropdownMenuItem>
            )}
            {onDisconnect && (
              <DropdownMenuItem
                data-testid={`connector-disconnect-${feedId}`}
                variant="destructive"
                className="rounded-[7px] px-2.5 py-2 text-[12.5px] font-semibold focus:bg-red-50"
                disabled={controlsBusy}
                onClick={onDisconnect}
              >
                <Unplug className="size-[13px]" /> Disconnect
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  )
}
